import { keyboardBox, useKeepFocusedVisible, useKeyboardInset } from '@/mobile/keyboard';
import { BackToOrigin } from '@/lib/origin';
import { clsx as cx, type ClassValue } from 'clsx';
import { HelpButton } from '@/components/help/HelpButton';
import { twMerge } from 'tailwind-merge';

/** clsx + tailwind-merge so a caller's className (e.g. w-44) overrides kit defaults (w-full). */
const clsx = (...c: ClassValue[]) => twMerge(cx(c));
import { Children, cloneElement, createContext, forwardRef, useCallback, useContext, isValidElement, useEffect, useId, useRef, useState, type ReactElement, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Loader2, SearchX, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { colorFor, initials } from '@/lib/format';
import { useUi } from '@/lib/store';
import { useNarrow } from '@/lib/useMedia';
import { inputHint } from '@/lib/localeHint';
import { DateTimeInput } from './DateTimeFields';
export { DatePickerButton } from './DateTimeFields';
import { ConfirmHost } from './confirm';
import { ReconnectingNote } from './ReconnectingNote';
export { ask, askText, choose, notice } from './confirm';
export { DialogFooter, useDialogSave } from './DialogFooter';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success' | 'link';
const variants: Record<Variant, string> = {
  primary: 'bg-accent font-semibold text-on-accent ring-1 ring-inset ring-black/10 hover:bg-accent-hover active:bg-accent-pressed shadow-sm disabled:bg-slate-200 disabled:text-slate-500 disabled:shadow-none disabled:ring-0 dark:disabled:bg-slate-800 dark:disabled:text-slate-400',
  secondary: 'bg-white text-slate-700 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 dark:bg-slate-800 dark:text-slate-100 dark:ring-slate-700 dark:hover:bg-slate-700',
  ghost: 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800',
  danger: 'bg-rose-600 text-white hover:bg-rose-700 shadow-sm',
  success: 'bg-emerald-700 text-white hover:bg-emerald-800 shadow-sm',
  // Text action in a sentence or card footer: underlined black text, never yellow text.
  link: 'text-slate-900 underline decoration-slate-400 underline-offset-2 hover:decoration-slate-900 dark:text-slate-100 !h-auto !px-0',
};

type Size = 'sm' | 'md' | 'icon';
/** Button classes for elements that must stay links (<Link>/<a>) but look like a button. */
export function buttonClass(variant: Variant = 'primary', size: Size = 'md', className?: string) {
  return clsx(
    'inline-flex items-center justify-center gap-1.5 rounded-md font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-black disabled:cursor-not-allowed disabled:opacity-60',
    size === 'sm' && 'h-7 px-2.5 text-xs', size === 'md' && 'h-9 px-3.5 text-sm', size === 'icon' && 'h-8 w-8 text-sm',
    variants[variant], className,
  );
}

const ICON_NAMES: Record<string, string> = {
  ChevronLeft: 'Previous', ChevronRight: 'Next', ChevronUp: 'Move up', ChevronDown: 'Move down', ArrowUp: 'Move up', ArrowDown: 'Move down',
  Trash: 'Delete', Trash2: 'Delete', Pencil: 'Edit', PenLine: 'Edit', X: 'Close', Plus: 'Add', Copy: 'Copy', Archive: 'Archive',
  MoreVertical: 'More actions', MoreHorizontal: 'More actions', Settings: 'Settings', Settings2: 'Settings', Search: 'Search', RefreshCw: 'Refresh',
  Download: 'Download', ExternalLink: 'Open', Undo2: 'Undo', Redo2: 'Redo', Check: 'Confirm',
};
/** Name of a lone lucide icon child (lucide sets displayName to the icon's name), mapped to an action label. */
function iconName(children: ReactNode): string | undefined {
  const only = Children.toArray(children).filter(c => isValidElement(c));
  if (only.length !== 1) return undefined;
  const t = (only[0] as ReactElement).type as { displayName?: string };
  return t?.displayName ? ICON_NAMES[t.displayName] : undefined;
}

export const Button = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: 'sm' | 'md' | 'icon'; loading?: boolean }>(
  ({ variant = 'primary', size = 'md', loading, className, children, disabled, ...rest }, ref) => (
    <button
      ref={ref}
      // Icon-only buttons need an accessible name: the tooltip text, else a name for common icons.
      aria-label={rest['aria-label'] ?? (size === 'icon' ? (typeof rest.title === 'string' ? rest.title : iconName(children)) : undefined)}
      disabled={disabled || loading}
      className={buttonClass(variant, size, className)}
      {...rest}
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
      {children}
    </button>
  ),
);
Button.displayName = 'Button';

const field = 'block w-full rounded-md border-0 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus-field dark:bg-slate-900 dark:text-slate-100 dark:ring-slate-700';

/**
 * t299u: desktop width caps for form controls (index.css, `.fw-*`). Short text and selects 320px, dates 180px,
 * date + time 300px, numbers 140px, long text and textareas 720px. The phone, modals, sheets, popovers and table
 * cells keep full-width fields; `width="full"` (or any max-w-* utility) opts a control out.
 */
export type FieldWidth = 'short' | 'long' | 'full';
export function fieldWidthClass(kind: 'input' | 'select' | 'textarea', type?: string, width?: FieldWidth): string {
  if (width === 'full') return '';
  if (width === 'long') return 'fw-long';
  if (width === 'short') return 'fw-short';
  if (kind === 'textarea') return 'fw-long';
  if (kind === 'select') return 'fw-short';
  if (type === 'number') return 'fw-num';
  if (type === 'date' || type === 'time') return 'fw-date';
  if (type === 'datetime-local') return 'fw-dt';
  if (type === 'checkbox' || type === 'radio' || type === 'range' || type === 'color' || type === 'file' || type === 'hidden') return '';
  return 'fw-short';
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { noHint?: boolean; width?: FieldWidth }>(({ className: cls, noHint, width, ...rest }, ref) => {
  const className = clsx(fieldWidthClass('input', rest.type, width), cls) || undefined;
  // Date/time fields on non-UK browsers: show the value the UK way next to the field (see lib/localeHint). Forms that
  // already spell the times out (the shift dialog's summary line) pass noHint.
  const hint = noHint ? null : inputHint(rest.type, rest.value);
  const hintId = useId();
  // Dates and times never use the browser's own fields: those follow the OS locale (10/22/2026, 10:00 PM) and ignore
  // the 12/24-hour preference. Ours always show en-GB dates and the person's clock.
  if (rest.type === 'date' || rest.type === 'time' || rest.type === 'datetime-local') {
    const { type, ...props } = rest;
    return <DateTimeInput ref={ref} kind={type as 'date' | 'time' | 'datetime-local'} fieldClass={field} className={className} {...props} />;
  }
  if (!hint) return <input ref={ref} className={clsx(field, className)} {...rest} />;
  return (
    <>
      <input ref={ref} className={clsx(field, className)} {...rest} aria-describedby={clsx(rest['aria-describedby'], hintId)} />
      <span id={hintId} className="mt-0.5 block whitespace-nowrap text-[11px] text-slate-500" data-testid="date-hint">{hint}</span>
    </>
  );
});
Input.displayName = 'Input';

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & { width?: FieldWidth }>(({ className, width, ...rest }, ref) => (
  <textarea ref={ref} className={clsx(field, 'resize-none', fieldWidthClass('textarea', undefined, width), className)} {...rest} />
));
Textarea.displayName = 'Textarea';

export function Select({ className, children, width, ...rest }: SelectHTMLAttributes<HTMLSelectElement> & { width?: FieldWidth }) {
  return <select className={clsx(field, 'pr-8', fieldWidthClass('select', undefined, width), className)} {...rest}>{children}</select>;
}

export function Label({ children, hint, htmlFor, id }: { children: ReactNode; hint?: ReactNode; htmlFor?: string; id?: string }) {
  return (
    <label htmlFor={htmlFor} id={id} className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">
      {children}{hint && <span className="ml-1 font-normal normal-case tracking-normal text-slate-500 dark:text-slate-400">{hint}</span>}
    </label>
  );
}

const LABELLABLE = new Set(['input', 'select', 'textarea']);
/**
 * Label + control. The label is tied to the control: a single native field (or our Input/Select/Textarea) gets a
 * generated id and `htmlFor`; anything else (a picker, a group of toggles) gets `aria-labelledby` on a group wrapper.
 */
export function Field({ label, hint, note, children, className }: { label: string; hint?: ReactNode; note?: ReactNode; children: ReactNode; className?: string }) {
  const uid = useId();
  const only = Children.count(children) === 1 && isValidElement(children) ? children as ReactElement<{ id?: string; 'aria-label'?: string; 'aria-describedby'?: string }> : null;
  const native = only && (only.type === Input || only.type === Select || only.type === Textarea || (typeof only.type === 'string' && LABELLABLE.has(only.type)));
  // t484u: `note` is a one-line muted hint under the control (described-by), for forms that keep hints off the label.
  const nid = `n${uid}`;
  const below = note ? <p id={nid} className="mt-1 text-xs text-slate-500 dark:text-slate-400">{note}</p> : null;
  if (native && only) {
    const id = only.props.id ?? `f${uid}`;
    const described = [only.props['aria-describedby'], note ? nid : null].filter(Boolean).join(' ') || undefined;
    return <div className={className}><Label hint={hint} htmlFor={id}>{label}</Label>{cloneElement(only, { id, 'aria-describedby': described })}{below}</div>;
  }
  const lid = `l${uid}`;
  return <div className={className} role="group" aria-labelledby={lid} aria-describedby={note ? nid : undefined}><Label hint={hint} id={lid}>{label}</Label>{children}{below}</div>;
}

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={clsx('h-5 w-5 animate-spin text-slate-500', className)} />;
}

export function Center({ children }: { children: ReactNode }) {
  return <div className="flex h-full w-full items-center justify-center p-8">{children}</div>;
}

const presenceColor = { online: 'bg-emerald-500', away: 'bg-orange-400', offline: 'bg-slate-300 dark:bg-slate-600' };

export function Avatar({ name, id, url, size = 32, presence }: { name?: string | null; id?: string; url?: string | null; size?: number; presence?: 'online' | 'away' | 'offline' }) {
  return (
    <span className="relative inline-flex shrink-0" style={{ width: size, height: size }}>
      {url ? (
        <img src={url} alt={name ?? ''} className="h-full w-full rounded-md object-cover" />
      ) : (
        <span className="flex h-full w-full items-center justify-center rounded-md font-semibold text-white" style={{ background: colorFor(id ?? name ?? '?'), fontSize: size * 0.38 }}>
          {initials(name)}
        </span>
      )}
      {presence && <span className={clsx('absolute -bottom-0.5 -right-0.5 rounded-full ring-white dark:ring-slate-900', size < 24 ? 'ring-1' : 'ring-2', presenceColor[presence])} style={{ width: Math.max(7, Math.round(size * 0.36)), height: Math.max(7, Math.round(size * 0.36)) }} />}
    </span>
  );
}

/** Badge call sites still pass the old pill tones; keep their text colour, drop fills, rings and text meant for a fill. */
export function unpill(cls?: string) {
  if (!cls) return cls;
  return cls.split(/\s+/).filter(t => {
    const u = t.split(':').pop()!.replace(/^!/, '');
    return !/^(bg-|ring|shadow|rounded|px-|py-|border)/.test(u) && !/^text-(white|on-accent|slate-900|slate-100)$/.test(u);
  }).join(' ');
}

export function Badge({ children, color, className }: { children: ReactNode; color?: string; className?: string }) {
  return (
    // House style (t344u): no pills. Muted text, with the colour as a small dot when there is one.
    <span className={clsx('inline-flex items-center gap-1 text-xs font-medium text-slate-500 dark:text-slate-400', unpill(className))}>
      {color && <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: color }} />}
      {children}
    </span>
  );
}

/**
 * t640u: desktop side sheets and drawers that open over the page (person, visitor, journey, members) have a visible ✕
 * in their top-right corner (aria-label "Close"). Escape and the backdrop close them too, and focus goes back to what
 * opened them (useSidePanelFocus). By default it sits absolutely in the sheet's corner (the sheet must be positioned);
 * `inline` puts it in a header row instead. Docked panels keep the quiet "Close" text button (CloseLink).
 */
export function SheetClose({ onClose, label = 'Close', inline }: { onClose: () => void; label?: string; inline?: boolean }) {
  return (
    <button type="button" onClick={onClose} aria-label="Close" title={label} data-testid="sheet-close"
      className={clsx('flex shrink-0 items-center justify-center rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white',
        !inline && 'absolute right-3 top-3 z-10')}>
      <X className="h-4 w-4" aria-hidden />
    </button>
  );
}

/** t640u: a side sheet takes focus when it opens and hands it back to the trigger when it closes. */
export function useSidePanelFocus(ref: { current: HTMLElement | null }, enabled = true) {
  useEffect(() => {
    if (!enabled) return;
    const prev = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    return () => { if (prev && document.contains(prev)) prev.focus?.(); };
  }, [ref, enabled]);
}

/** t344u: docked desktop panels (help, assistant, threads, task) close with a quiet "Close" text button, not a ✕. */
/**
 * t707u: a side panel's close button: the X icon, same size and style as a dialog's (Esc still closes the panel).
 * Its accessible name is always "Close"; `label` ("Close thread") is the hover text.
 */
export function CloseLink({ onClose, label = 'Close', className }: { onClose: () => void; label?: string; className?: string }) {
  return (
    <button type="button" onClick={onClose} aria-label="Close" title={label} data-testid="panel-close"
      className={clsx('shrink-0 rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200', className)}>
      <X className="h-4 w-4" aria-hidden />
    </button>
  );
}

export function CountPill({ n, tone = 'brand' }: { n: number; tone?: 'brand' | 'red' }) {
  if (!n) return null;
  return <span className={clsx('min-w-5 rounded-full px-1.5 text-center text-[11px] font-bold leading-5', tone === 'red' ? 'bg-rose-600 text-white' : 'bg-accent text-on-accent')}>{n > 99 ? '99+' : n}</span>;
}

// Open dialogs, innermost last: Escape and focus trapping only act on the top one (B10).
// Nesting depth comes from context (a child dialog's effect runs before its parent's), then most recently opened wins.
const modalStack: { id: symbol; depth: number; at: number }[] = [];
let modalSeq = 0;
const ModalDepth = createContext(0);
const isTopModal = (id: symbol) => {
  const top = [...modalStack].sort((a, b) => b.depth - a.depth || b.at - a.at)[0];
  return top?.id === id;
};
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({ open, onClose, title, children, footer, wide }: { open: boolean; onClose: () => void; title: ReactNode; children: ReactNode; footer?: ReactNode; wide?: boolean | 'lg' | 'xl' }) {
  const titleId = useId();
  const depth = useContext(ModalDepth) + 1;
  const panel = useRef<HTMLDivElement>(null);
  const overlay = useRef<HTMLDivElement>(null);
  const kb = useKeyboardInset();
  useKeepFocusedVisible(overlay, kb, open);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!open) return;
    const me = Symbol('modal');
    modalStack.push({ id: me, depth, at: ++modalSeq });
    const restore = document.activeElement as HTMLElement | null;
    // Initial focus: the first field in the body, else the first focusable control, else the panel.
    const t = setTimeout(() => {
      const el = panel.current;
      if (!el || el.contains(document.activeElement)) return;
      const body = el.querySelector('.modal-body');
      const first = el.querySelector<HTMLElement>('[data-autofocus], [autofocus]')
        ?? body?.querySelector<HTMLElement>('input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])')
        ?? body?.querySelector<HTMLElement>(FOCUSABLE)
        ?? el;
      first.focus({ preventScroll: true });
    }, 0);
    const h = (e: KeyboardEvent) => {
      if (!isTopModal(me)) return;
      if (e.key === 'Escape') { e.stopPropagation(); close.current(); return; }
      if (e.key !== 'Tab' || !panel.current) return;
      const items = [...panel.current.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(x => x.offsetParent !== null);
      if (!items.length) { e.preventDefault(); return; }
      const first = items[0], last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === panel.current)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', h);
    return () => {
      clearTimeout(t);
      window.removeEventListener('keydown', h);
      const i = modalStack.findIndex(x => x.id === me);
      if (i >= 0) modalStack.splice(i, 1);
      if (restore && document.contains(restore)) restore.focus();
    };
  }, [open, depth]);
  if (!open) return null;
  return createPortal(
    // t207u: while the on-screen keyboard is up the overlay is pinned to the visible area, so the focused field stays above it.
    <div ref={overlay} className={clsx('fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4 backdrop-blur-sm', kb.inset ? 'pt-4' : 'pt-[10vh]')} style={keyboardBox(kb)} onMouseDown={() => close.current()}>
      <div ref={panel} tabIndex={-1} className={clsx('w-full rounded-xl bg-white shadow-2xl ring-1 ring-slate-200 outline-none dark:bg-slate-900 dark:ring-slate-800', wide === 'xl' ? 'max-w-6xl' : wide === 'lg' ? 'max-w-4xl' : wide ? 'max-w-3xl' : 'max-w-lg', (wide === 'xl' || wide === 'lg') && 'modal-wide')} onMouseDown={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3.5 dark:border-slate-800">
          <h2 id={titleId} className="text-base font-semibold">{title}</h2>
          <button onClick={onClose} className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800" aria-label="Close"><X className="h-4 w-4" /></button>
        </div>
        <div className="modal-body max-h-[70vh] overflow-y-auto px-5 py-4 scroll-thin empty:hidden"><ModalDepth.Provider value={depth}>{children}</ModalDepth.Provider></div>
        {/* t261u: a question with no body ("Reassign from Jane to Tom?") is just the title and the buttons. */}
        {footer && <div className="flex flex-wrap justify-end gap-2 border-t border-slate-200 px-5 py-3 dark:border-slate-800 [.modal-body:empty+&]:border-t-0">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

/** Accessible tab strip: role=tablist, arrow/Home/End keys, scrolls sideways on narrow screens. */
/**
 * Tab strip. When the tabs don't fit (narrow dialogs, phones) the row scrolls sideways: the clipped edge fades
 * out and a small arrow scrolls it, and the selected tab is always scrolled into view.
 */
export function Tabs<T extends string>({ tabs, value, onChange, className, label }: { tabs: { id: T; label: ReactNode; count?: number }[]; value: T; onChange: (v: T) => void; className?: string; label?: string }) {
  const strip = useRef<HTMLDivElement>(null);
  const [edge, setEdge] = useState({ l: false, r: false });
  useEffect(() => {
    const el = strip.current;
    if (!el) return;
    const update = () => {
      const l = el.scrollLeft > 1, r = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
      setEdge(e => (e.l === l && e.r === r ? e : { l, r }));
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    ro?.observe(el);
    for (const c of Array.from(el.children)) ro?.observe(c);
    return () => { el.removeEventListener('scroll', update); ro?.disconnect(); };
  }, [tabs.length]);
  useEffect(() => {
    const el = strip.current;
    const btn = el?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!el || !btn || el.scrollWidth <= el.clientWidth) return;
    const pad = 32;
    if (btn.offsetLeft - pad < el.scrollLeft) el.scrollTo({ left: btn.offsetLeft - pad });
    else if (btn.offsetLeft + btn.offsetWidth + pad > el.scrollLeft + el.clientWidth) el.scrollTo({ left: btn.offsetLeft + btn.offsetWidth + pad - el.clientWidth });
  }, [value]);
  const onKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const i = tabs.findIndex(t => t.id === value);
    const next = e.key === 'ArrowRight' ? (i + 1) % tabs.length : e.key === 'ArrowLeft' ? (i - 1 + tabs.length) % tabs.length : e.key === 'Home' ? 0 : e.key === 'End' ? tabs.length - 1 : -1;
    if (next < 0) return;
    e.preventDefault();
    const list = e.currentTarget;
    onChange(tabs[next].id);
    requestAnimationFrame(() => list.querySelectorAll<HTMLElement>('[role="tab"]')[next]?.focus());
  };
  const page = (dir: 1 | -1) => { const el = strip.current; if (el) el.scrollBy({ left: dir * Math.max(120, el.clientWidth * 0.6), behavior: 'smooth' }); };
  const fade = 40;
  const mask = edge.l || edge.r
    ? `linear-gradient(to right, ${edge.l ? `transparent, #000 ${fade}px` : '#000'}, ${edge.r ? `#000 calc(100% - ${fade}px), transparent` : '#000'})`
    : undefined;
  const arrow = 'absolute top-1/2 z-10 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm hover:text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:text-white';
  return (
    <div className={clsx('tab-strip relative min-w-0', className)} data-testid="tabs" data-overflow={edge.l || edge.r ? 'true' : undefined}>
      <div ref={strip} role="tablist" aria-label={label} onKeyDown={onKey}
        className="scroll-none flex gap-1 overflow-x-auto" style={mask ? { maskImage: mask, WebkitMaskImage: mask } : undefined}>
        {tabs.map(t => (
          <button key={t.id} type="button" role="tab" aria-selected={value === t.id} tabIndex={value === t.id ? 0 : -1} onClick={() => onChange(t.id)}
            className={clsx('flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-[3px] px-3 py-2 text-sm font-medium transition',
              value === t.id ? 'border-accent-line font-semibold text-slate-900 dark:text-white' : 'border-transparent text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200')}>
            {t.label}{t.count ? <span className="text-xs font-normal text-slate-500 dark:text-slate-400">{t.count > 99 ? '99+' : t.count}</span> : null}
          </button>
        ))}
      </div>
      {edge.l && <button type="button" tabIndex={-1} aria-label="Scroll tabs left" title="More tabs" onClick={() => page(-1)} className={clsx(arrow, 'left-0')} data-testid="tabs-scroll-left"><ChevronLeft className="h-3.5 w-3.5" /></button>}
      {edge.r && <button type="button" tabIndex={-1} aria-label="Scroll tabs right" title="More tabs" onClick={() => page(1)} className={clsx(arrow, 'right-0')} data-testid="tabs-scroll-right"><ChevronRight className="h-3.5 w-3.5" /></button>}
    </div>
  );
}

/**
 * UI review: two page widths. *Workspace* pages (schedule, boards, Issues, Fleet, reports) use the full width;
 * *Document* pages (lists, forms, settings, profiles) centre at max-w-5xl. Long-form reading (articles,
 * announcements) may use `reading`. t343u: two-column pages (Visitors, the dashboard) centre at `wide` (max-w-6xl).
 * Use these instead of ad-hoc max-w-* values.
 */
export const PAGE_WIDTH = { workspace: 'w-full', document: 'mx-auto w-full max-w-5xl', wide: 'mx-auto w-full max-w-6xl', reading: 'mx-auto w-full max-w-3xl', form: 'mx-auto w-full form-width' } as const;

/** t294u: true inside the phone Filters sheet, so controls there (DateRangeFilter) show their touch layout. */
export const FilterSheetContext = createContext(false);

/**
 * UI review: the one filter bar. On desktop the filters sit in one wrapping row at their natural width (no
 * full-width stacked selects); on phones they collapse behind a "Filters (n)" button that opens a sheet.
 * `active` is how many filters differ from their defaults; `onClear` resets them.
 */
export function FilterBar({ children, active = 0, onClear, trailing, className }: { children: ReactNode; active?: number; onClear?: () => void; trailing?: ReactNode; className?: string }) {
  const narrow = useNarrow();
  const [open, setOpen] = useState(false);
  if (narrow) {
    return (
      <div className={clsx('flex items-center gap-2', className)} data-testid="filter-bar">
        <Button variant="secondary" size="sm" onClick={() => setOpen(true)} aria-haspopup="dialog">Filters{active ? ` (${active})` : ''}</Button>
        {active > 0 && onClear && <Button variant="ghost" size="sm" onClick={onClear}>Clear</Button>}
        <div className="ml-auto flex items-center gap-2">{trailing}</div>
        <Modal open={open} onClose={() => setOpen(false)} title="Filters" footer={<><Button variant="ghost" onClick={() => { onClear?.(); }}>Clear</Button><Button onClick={() => setOpen(false)}>Done</Button></>}>
          <FilterSheetContext.Provider value={true}><div className="flex flex-col gap-3 [&_input]:w-full [&_select]:w-full">{children}</div></FilterSheetContext.Provider>
        </Modal>
      </div>
    );
  }
  return (
    <div className={clsx('flex flex-wrap items-center gap-2 [&>input]:w-auto [&>input]:min-w-[12rem] [&>select]:w-auto [&>select]:min-w-[9rem]', className)} data-testid="filter-bar">
      {children}
      {active > 0 && onClear && <Button variant="ghost" size="sm" onClick={onClear}>Clear filters</Button>}
      {trailing && <div className="ml-auto flex items-center gap-2">{trailing}</div>}
    </div>
  );
}
export function EmptyState({ icon, title, children, action }: { icon?: ReactNode; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 p-10 text-center dark:border-slate-700">
      {icon && <div className="text-slate-500">{icon}</div>}
      <div className="font-semibold">{title}</div>
      {children && <div className="max-w-md text-sm text-slate-500">{children}</div>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

export function Card({ children, className, title, actions }: { children: ReactNode; className?: string; title?: ReactNode; actions?: ReactNode }) {
  return (
    <section className={clsx('rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900', className)}>
      {(title || actions) && (
        <header className="flex items-center justify-between border-b border-slate-100 px-4 py-3 dark:border-slate-800">
          <h3 className="text-sm font-semibold">{title}</h3>
          <div className="flex items-center gap-2">{actions}</div>
        </header>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

export function Stat({ label, value, sub }: { label: string; value: ReactNode; sub?: ReactNode }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

export function PageHeader({ title, subtitle, actions, icon }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode; icon?: ReactNode }) {
  return (
    // Phones: the title and the ? share the first row and actions wrap below. Wider: title, actions, then ?.
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-slate-200 bg-white px-4 py-3 sm:px-6 sm:py-4 dark:border-slate-800 dark:bg-slate-950" data-testid="page-header">
      <div className="order-1 flex min-w-0 flex-1 items-center gap-3">
        {/* Opened from Home (or another origin): a back arrow on phones, "← Back to Home" above the title on wider screens. */}
        <span className="-ml-1 sm:hidden"><BackToOrigin compact className="p-1" /></span>
        {icon && <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent text-on-accent" aria-hidden>{icon}</div>}
        <div className="min-w-0">
          <span className="hidden sm:block"><BackToOrigin className="text-xs" /></span>
          <h1 className="truncate text-lg font-semibold">{title}</h1>
          {subtitle && <p className="truncate text-sm text-slate-500">{subtitle}</p>}
        </div>
      </div>
      {/* t694u: header selects size to their content (not the 20rem field width) so a select and two buttons share one row;
          they only wrap when the screen is genuinely narrow. */}
      {actions && <div className="order-3 flex basis-full flex-wrap items-center gap-2 sm:order-2 sm:basis-auto [&>select]:w-auto [&>select]:max-w-[16rem]" data-testid="page-header-actions">{actions}</div>}
      <HelpButton className="order-2 sm:order-3" />
    </div>
  );
}

export function Toaster() {
  const toasts = useUi(s => s.toasts);
  const dismiss = useUi(s => s.dismissToast);
  return <>{createPortal(
    <div role="status" aria-live="polite" className="pointer-events-none fixed bottom-20 right-4 z-[60] flex w-[calc(100%-2rem)] flex-col gap-2 sm:w-80 md:bottom-4">
      {toasts.map(t => (
        <div key={t.id} className={clsx('pointer-events-auto flex items-start gap-2 rounded-lg px-4 py-3 text-sm shadow-lg ring-1',
          t.tone === 'error' ? 'bg-rose-50 text-rose-800 ring-rose-200 dark:bg-rose-900/40 dark:text-rose-200 dark:ring-rose-800' : t.tone === 'success' ? 'bg-emerald-50 text-emerald-800 ring-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200 dark:ring-emerald-800' : 'bg-white text-slate-800 ring-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:ring-slate-700')}>
          <span className="flex-1">{t.text}</span>
          {t.action && <button type="button" onClick={() => { t.action!.run(); dismiss(t.id); }} className="font-semibold underline underline-offset-2 hover:no-underline">{t.action.label}</button>}
          <button onClick={() => dismiss(t.id)} className="opacity-60 hover:opacity-100" aria-label="Dismiss"><X className="h-4 w-4" /></button>
        </div>
      ))}
      <ReconnectingNote />
    </div>,
    document.body,
  )}<ConfirmHost /></>;
}

export function Menu({ trigger, children, align = 'right', up, open, setOpen, fixed }: { trigger: ReactNode; children: ReactNode; align?: 'left' | 'right'; up?: boolean; open: boolean; setOpen: (v: boolean) => void;
  /** t546u: pin the list to the window instead of the trigger, so a menu inside a scrolling table never widens or scrolls it (opens upwards near the bottom). */
  fixed?: boolean }) {
  const anchor = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState<{ top?: number; bottom?: number; left?: number; right?: number } | null>(null);
  const place = useCallback(() => {
    const r = anchor.current?.getBoundingClientRect();
    if (!r) return;
    const upwards = up || r.bottom + 220 > window.innerHeight;
    setAt({ ...(upwards ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }), ...(align === 'right' ? { right: window.innerWidth - r.right } : { left: r.left }) });
  }, [up, align]);
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const t = setTimeout(() => window.addEventListener('click', close), 0);
    // Fixed: follows its trigger when the table or page scrolls.
    if (fixed) { place(); window.addEventListener('scroll', place, true); window.addEventListener('resize', place); }
    return () => { clearTimeout(t); window.removeEventListener('click', close); window.removeEventListener('scroll', place, true); window.removeEventListener('resize', place); };
  }, [open, setOpen, fixed, place]);
  const toggle = () => { if (!open && fixed) place(); setOpen(!open); };
  return (
    <div className="relative" ref={anchor}>
      <div onClick={toggle}>{trigger}</div>
      {open && (
        <div style={fixed && at ? { position: 'fixed', ...at } : undefined}
          className={clsx('z-40 min-w-48', fixed ? 'fixed' : ['absolute', up ? 'bottom-full mb-1' : 'mt-1', align === 'right' ? 'right-0' : 'left-0'], 'on-page overflow-hidden rounded-lg bg-white py-1 shadow-xl ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700')}>
          {children}
        </div>
      )}
    </div>
  );
}

export function MenuItem({ children, onClick, danger, icon, disabled, title, 'data-testid': testId }: { children: ReactNode; onClick: () => void; danger?: boolean; icon?: ReactNode; disabled?: boolean; title?: string; 'data-testid'?: string }) {
  return (
    <button onClick={onClick} disabled={disabled} title={title} aria-disabled={disabled || undefined} data-testid={testId} className={clsx('flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent dark:hover:bg-slate-700', danger ? 'text-rose-600' : 'text-slate-700 dark:text-slate-200')}>
      {icon}{children}
    </button>
  );
}

export function Toggle({ checked, onChange, label, disabled, testId }: { checked: boolean; onChange: (v: boolean) => void; label?: ReactNode; disabled?: boolean; testId?: string }) {
  return (
    <label className={clsx('inline-flex items-center gap-2 text-sm', disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer')}>
      <button type="button" role="switch" aria-checked={checked} disabled={disabled} data-testid={testId} onClick={() => { if (!disabled) onChange(!checked); }}
        className={clsx('relative h-5 w-9 rounded-full transition', checked ? 'bg-accent ring-1 ring-inset ring-ink' : 'bg-slate-300 dark:bg-slate-700')}>
        <span className={clsx('absolute top-0.5 h-4 w-4 rounded-full shadow transition', checked ? 'left-4 bg-ink' : 'left-0.5 bg-white')} />
      </button>
      {label}
    </label>
  );
}

export function ErrorBox({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  if (!error) return null;
  return (
    <div role="alert" className="flex items-center gap-3 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700 ring-1 ring-rose-200 dark:bg-rose-950 dark:text-rose-200 dark:ring-rose-900">
      <span className="flex-1">{error instanceof Error ? error.message : String(error)}</span>
      {onRetry && <button type="button" className="shrink-0 font-medium underline" onClick={onRetry}>Try again</button>}
    </div>
  );
}

/**
 * UI review: the standard "this item doesn't exist / isn't shared with you" page, styled like the access-denied page.
 * Use it for item pages instead of a bare grey line.
 */
export function NotFound({ title = 'Not found', children, back }: { title?: string; children?: ReactNode; back?: { to: string; label: string } }) {
  return (
    <div className="flex h-full items-center justify-center p-8" data-testid="not-found">
      <div className="max-w-sm text-center">
        <SearchX className="mx-auto mb-3 h-10 w-10 text-slate-500" aria-hidden />
        <h1 className="text-lg font-semibold text-slate-900 dark:text-white">{title}</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{children ?? 'It may have been deleted, or it isn’t shared with you.'}</p>
        <Link to={back?.to ?? '/'} className="mt-4 inline-block rounded-md bg-brand-yellow px-3 py-1.5 text-sm font-medium text-on-accent-strong">{back?.label ?? 'Back to Home'}</Link>
      </div>
    </div>
  );
}

export { DataTable, sortRows, type Column } from './DataTable';
export { QuickSearch, type QuickSearchProps } from './QuickSearch';
export { HoverBubble } from './HoverBubble';

/**
 * UI review #15: one sticky Save per settings page. Shows only with unsaved changes, sticks to the bottom of the
 * scroll area, and warns before leaving the page with unsaved edits.
 */
export function SaveBar({ dirty, saving, onSave, onReset, label = 'Save changes' }: { dirty: boolean; saving?: boolean; onSave: () => void; onReset?: () => void; label?: string }) {
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  if (!dirty) return null;
  return (
    <div role="region" aria-label="Unsaved changes" data-testid="save-bar"
      className="sticky bottom-0 z-20 -mx-4 mt-4 flex items-center justify-end gap-2 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95">
      <span className="mr-auto text-sm text-slate-600 dark:text-slate-300">You have unsaved changes</span>
      {onReset && <Button variant="ghost" onClick={onReset} disabled={saving}>Discard</Button>}
      <Button onClick={onSave} loading={saving}>{label}</Button>
    </div>
  );
}

/** Small confirm step (feedback #4 / #6): destructive actions like removing a position or retiring a type. */
export function ConfirmDialog({ open, title, children, confirmLabel = 'Confirm', danger = true, busy, onConfirm, onCancel }: {
  open: boolean; title: string; children?: ReactNode; confirmLabel?: string; danger?: boolean; busy?: boolean; onConfirm: () => void; onCancel: () => void;
}) {
  return (
    <Modal open={open} onClose={onCancel} title={title}
      footer={<><Button variant="ghost" onClick={onCancel}>Cancel</Button><Button variant={danger ? 'danger' : 'primary'} loading={busy} onClick={onConfirm} data-testid="confirm-ok">{confirmLabel}</Button></>}>
      <div className="text-sm text-slate-600 dark:text-slate-300">{children}</div>
    </Modal>
  );
}
